import React, { useEffect, useState } from 'react';
import { ArrowUpRight, MessageCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CityStrip } from '@/components/site/CityStrip';
import { WRAP, SECTION, H2_SM } from '@/components/site/ServicePageParts';
import { SITE } from '@/data/site';

const BUDGETS = ['< ₹5L', '₹5–15L', '₹15–35L', '₹35L+', 'Not sure yet'];

// Swap this for your Calendly / Cal.com / Savvycal embed URL.
// e.g. https://cal.com/niodelta/discovery or https://calendly.com/niodelta/30min?embed_domain=...
const CALENDAR_EMBED_URL = 'https://cal.com/niodelta/discovery?embed=true&theme=dark';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const puneTime = () => {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
  } catch (e) {
    return null;
  }
};

/** Live Pune clock, refreshed every 20 seconds. */
function usePuneClock() {
  const [time, setTime] = useState(puneTime);
  useEffect(() => {
    const id = window.setInterval(() => setTime(puneTime()), 20000);
    return () => window.clearInterval(id);
  }, []);
  return time;
}

const RowArrow = () => (
  <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:rotate-45">
      <path d="M4 12 12 4M5 4h7v7" />
    </svg>
  </span>
);

const ROW = 'group relative isolate flex items-center justify-between gap-6 py-7 md:py-8';
const ROW_HOVER = 'absolute inset-y-0 -inset-x-4 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100';
const ROW_VALUE = 'mt-2 font-display font-semibold text-[clamp(24px,2.6vw,38px)] tracking-[-0.04em] leading-none text-white break-words transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-2';

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', budget: BUDGETS[1], message: '',
  });
  const [sent, setSent] = useState(false);
  const [touched, setTouched] = useState({});
  const [tried, setTried] = useState(false);
  const clock = usePuneClock();

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('A name, email and a few lines is all we need.');
      return;
    }
    const text =
      `Hi NioDelta — ${form.name} here${form.company ? ` from ${form.company}` : ''}.%0A` +
      `Budget: ${form.budget}.%0A%0A` +
      `${form.message}%0A%0A` +
      `Reply to: ${form.email}`;
    window.open(`${SITE.whatsapp}?text=${text}`, '_blank', 'noopener');
    setSent(true);
    toast.success('Opening WhatsApp with your brief…');
  };

  // Inline validation is presentational only; `submit` keeps its own checks.
  const onSubmit = (e) => {
    setTried(true);
    submit(e);
  };

  const errors = {
    name: !form.name.trim() ? 'Your name, so we know who to reply to.' : '',
    email: !form.email.trim()
      ? 'An email for the reply.'
      : !EMAIL_RE.test(form.email.trim()) ? 'That email looks incomplete.' : '',
    message: !form.message.trim() ? 'A few lines is enough. Plain words are fine.' : '',
  };
  const showErr = (k) => (tried || touched[k]) && errors[k];

  return (
    <div data-testid="contact-page">
      <Toaster theme="dark" position="bottom-right" />

      {/* HERO: split — headline + channels left, form right */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-28 md:pt-36 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-14`}>
          {/* A — headline */}
          <div className="lg:col-span-6 lg:col-start-1 lg:row-start-1">
            <SectionLabel as="h1" num="01" data-testid="contact-eyebrow">
              Contact NioDelta — Web &amp; App Developers in Pune, Mumbai, Nashik
            </SectionLabel>
            <WordReveal
              as="p"
              text="Tell us what you’re building. We reply within a business day."
              accent="business"
              data-testid="contact-headline"
              className="mt-8 max-w-[13ch] font-display font-semibold text-[clamp(44px,6.2vw,108px)] leading-[0.92] tracking-[-0.05em]"
            />
            <Reveal delay={420}>
              <p className="mt-10 text-lg md:text-xl text-[#C9C9D3] max-w-[44ch] leading-relaxed">
                No SDR, no auto-reply, no form-to-nowhere. The note lands with a partner,
                and one of us writes back—personally—within one working day. Promise.
              </p>
            </Reveal>
            <Reveal delay={520}>
              <div className="mt-8 nd-mono flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="nd-live" aria-hidden />
                <span><span className="text-white">Reply</span> within 1 business day · IST 9–7</span>
              </div>
            </Reveal>
          </div>

          {/* B — form */}
          <div className="lg:col-span-6 lg:col-start-7 lg:row-start-1 lg:row-span-2 relative">
            <div className="lg:sticky lg:top-28">
              <div aria-hidden className="pointer-events-none absolute -inset-6 md:-inset-10">
                <div
                  data-testid="contact-form-glow"
                  className="absolute inset-0 opacity-60 blur-3xl contact-glow"
                  style={{
                    background:
                      'radial-gradient(45% 55% at 25% 30%, rgba(108,92,231,0.4) 0%, transparent 70%),' +
                      'radial-gradient(40% 50% at 80% 80%, rgba(75,159,255,0.26) 0%, transparent 70%)',
                  }}
                />
              </div>

              <Reveal delay={200}>
                <form
                  data-testid="contact-form"
                  onSubmit={onSubmit}
                  className="relative rounded-[18px] border border-white/[0.08] bg-ink-surface/90 backdrop-blur-sm p-6 sm:p-8 md:p-10"
                >
                  <div className="flex items-baseline justify-between gap-4 mb-10">
                    <span className="nd-mono !text-white">— Project brief</span>
                    <span className="nd-mono">5 fields · 90 seconds</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-9">
                    <Field n="01" label="Your name" id="contact-name" error={showErr('name')}>
                      <input
                        id="contact-name"
                        data-testid="contact-name"
                        required
                        value={form.name}
                        onChange={update('name')}
                        onBlur={touch('name')}
                        placeholder="Asha Krishnan"
                        className={fieldCls(showErr('name'))}
                        autoComplete="name"
                        aria-invalid={showErr('name') ? true : undefined}
                        aria-describedby={showErr('name') ? 'contact-name-err' : undefined}
                      />
                    </Field>
                    <Field n="02" label="Email" id="contact-email" error={showErr('email')}>
                      <input
                        id="contact-email"
                        data-testid="contact-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={update('email')}
                        onBlur={touch('email')}
                        placeholder="asha@company.in"
                        className={fieldCls(showErr('email'))}
                        autoComplete="email"
                        aria-invalid={showErr('email') ? true : undefined}
                        aria-describedby={showErr('email') ? 'contact-email-err' : undefined}
                      />
                    </Field>
                    <Field n="03" label="Company (optional)" id="contact-company" className="sm:col-span-2">
                      <input
                        id="contact-company"
                        data-testid="contact-company"
                        value={form.company}
                        onChange={update('company')}
                        placeholder="What are you building?"
                        className={fieldCls(false)}
                        autoComplete="organization"
                      />
                    </Field>

                    <div className="sm:col-span-2" role="group" aria-labelledby="contact-budget-label">
                      <span id="contact-budget-label" className="nd-mono flex items-center gap-3 mb-4">
                        <span className="text-white tabular-nums">04</span>Budget range
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {BUDGETS.map((b) => (
                          <button
                            key={b}
                            type="button"
                            aria-pressed={form.budget === b}
                            data-testid={`contact-budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                            onClick={() => setForm((s) => ({ ...s, budget: b }))}
                            className={`px-4 py-2.5 rounded-full border text-sm transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet/70 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface ${
                              form.budget === b
                                ? 'border-white bg-white text-ink-bg'
                                : 'border-white/[0.12] text-ink-muted hover:text-white hover:border-white/30'
                            }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Field n="05" label="A few lines about the project" id="contact-message" error={showErr('message')} className="sm:col-span-2">
                      <textarea
                        id="contact-message"
                        data-testid="contact-message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={update('message')}
                        onBlur={touch('message')}
                        placeholder="Audience, the rough goal, anything that already exists. Plain words are fine."
                        className={`${fieldCls(showErr('message'))} resize-none leading-relaxed`}
                        aria-invalid={showErr('message') ? true : undefined}
                        aria-describedby={showErr('message') ? 'contact-message-err' : undefined}
                      />
                    </Field>
                  </div>

                  {sent && (
                    <div role="status" data-testid="contact-success" className="mt-10 flex items-start gap-3 rounded-[12px] border border-[#3DDC97]/25 bg-[#3DDC97]/[0.06] px-4 py-3.5">
                      <span className="nd-live mt-1.5 shrink-0" aria-hidden />
                      <p className="text-sm text-[#C9C9D3] leading-relaxed">
                        Your brief is drafted in WhatsApp. Send it from there and a partner replies within one business day.
                      </p>
                    </div>
                  )}

                  <div className="mt-10 pt-8 border-t border-white/[0.08] flex flex-wrap items-center justify-between gap-6">
                    <p className="text-xs text-ink-muted max-w-[34ch] leading-relaxed">
                      Hitting send opens WhatsApp with your brief drafted—you can edit before sending,
                      or copy and email it instead.
                    </p>
                    <MagneticButton
                      variant="primary"
                      data-testid="contact-submit"
                      onClick={onSubmit}
                    >
                      {sent ? 'Sent — reopen WhatsApp' : 'Send brief'}
                    </MagneticButton>
                  </div>
                </form>
              </Reveal>
            </div>
          </div>

          {/* C — direct channels */}
          <div className="lg:col-span-6 lg:col-start-1 lg:row-start-2">
            <Reveal>
              <span className="nd-mono">— Or skip the form</span>
            </Reveal>
            <ul className="mt-6 border-t border-white/[0.08]">
              <Reveal as="li" className="border-b border-white/[0.08]">
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-direct-whatsapp"
                  className={ROW}
                >
                  <span aria-hidden className={ROW_HOVER} />
                  <div className="min-w-0">
                    <span className="nd-mono">WhatsApp</span>
                    <div className={ROW_VALUE}>+91 90678 20204</div>
                    <div className="mt-2.5 text-sm text-ink-muted">Fastest. Usually within hours.</div>
                  </div>
                  <RowArrow />
                </a>
              </Reveal>
              <Reveal as="li" delay={60} className="border-b border-white/[0.08]">
                <a href={`mailto:${SITE.email}`} data-testid="contact-direct-email" className={ROW}>
                  <span aria-hidden className={ROW_HOVER} />
                  <div className="min-w-0">
                    <span className="nd-mono">Email</span>
                    <div className={ROW_VALUE}>{SITE.email}</div>
                    <div className="mt-2.5 text-sm text-ink-muted">RFPs, NDAs, longer briefs.</div>
                  </div>
                  <RowArrow />
                </a>
              </Reveal>
              <Reveal as="li" delay={120} className="border-b border-white/[0.08]">
                <a href={`tel:${SITE.phone.replace(/[^+\d]/g, '')}`} data-testid="contact-direct-phone" className={ROW}>
                  <span aria-hidden className={ROW_HOVER} />
                  <div className="min-w-0">
                    <span className="nd-mono">Phone</span>
                    <div className={ROW_VALUE}>{SITE.phone.replace(/-/g, ' ')}</div>
                    <div className="mt-2.5 text-sm text-ink-muted">Calls during studio hours, IST 9–7.</div>
                  </div>
                  <RowArrow />
                </a>
              </Reveal>
              <Reveal as="li" delay={180} className="border-b border-white/[0.08]">
                <div data-testid="contact-direct-location" className="flex items-center justify-between gap-6 py-7 md:py-8">
                  <div className="min-w-0">
                    <span className="nd-mono">Studios</span>
                    <div className="mt-2 font-display font-semibold text-[clamp(24px,2.6vw,38px)] tracking-[-0.04em] leading-none text-white">{SITE.location}</div>
                    <div className="mt-2.5 text-sm text-ink-muted">Studio in Pune. On-site visits in Mumbai &amp; Nashik.</div>
                  </div>
                  {clock && (
                    <div className="shrink-0 text-right" aria-label={`Local time in Pune ${clock}`}>
                      <div className="font-display font-medium text-[clamp(24px,2.4vw,34px)] tracking-[-0.04em] leading-none tabular-nums">{clock}</div>
                      <div className="mt-2 nd-mono">Pune · IST</div>
                    </div>
                  )}
                </div>
              </Reveal>
            </ul>

            <Reveal delay={120}>
              <div className="mt-10 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <span className="nd-mono pt-1">— Capacity</span>
                <p className="text-sm text-ink-muted leading-relaxed max-w-[46ch]">
                  We take on <span className="text-white">3 engagements per quarter</span>. If we’re booked
                  we’ll tell you on day one and recommend a studio we trust.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CALENDAR EMBED */}
      <section data-testid="contact-calendar" className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionLabel num="02">Discovery call</SectionLabel>
              <Reveal>
                <h2 className={`mt-7 ${H2_SM}`}>
                  Pick a slot. <span className="italic text-violet">Thirty minutes.</span>
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-6 text-ink-muted leading-relaxed max-w-sm">
                  The call is with a partner—no SDR, no deck. Bring the URL of the product you wish yours
                  behaved like, or just an honest problem statement.
                </p>
              </Reveal>
              <ul className="mt-8 border-t border-white/[0.08] text-sm">
                {['30 minutes · Google Meet', 'No NDA needed for the first call', 'A partner picks up — not a salesperson'].map((t, i) => (
                  <li key={t} className="flex gap-4 py-3.5 border-b border-white/[0.08]">
                    <span className="nd-mono tabular-nums">0{i + 1}</span>
                    <span className="text-[#C9C9D3]">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <MagneticButton
                  href={SITE.whatsapp}
                  external
                  variant="ghost"
                  data-testid="contact-calendar-whatsapp"
                >
                  <MessageCircle size={15} />
                  Or DM us first
                </MagneticButton>
              </div>
            </div>
          </div>

          <Reveal className="lg:col-span-8" delay={120}>
            <div className="relative rounded-[18px] border border-white/[0.08] bg-ink-surface overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-white/[0.08]">
                <span className="nd-mono flex items-center gap-2.5"><span className="nd-live" aria-hidden />NioDelta · 30 min discovery</span>
                <a
                  href={CALENDAR_EMBED_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-calendar-open"
                  className="nd-mono nd-link-u !text-white/85 hover:!text-white"
                >
                  Open in new tab <ArrowUpRight size={12} />
                </a>
              </div>
              <div className="relative aspect-[4/5] sm:aspect-[4/3] md:aspect-[16/10]">
                <iframe
                  data-testid="contact-calendar-iframe"
                  title="Book a 30-minute discovery call with NioDelta"
                  src={CALENDAR_EMBED_URL}
                  className="absolute inset-0 h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allow="camera; microphone; clipboard-write"
                />
              </div>
            </div>
            <p className="mt-4 nd-mono !normal-case !tracking-normal text-ink-muted">
              Calendar runs on <code className="text-white">cal.com/niodelta</code>. If the embed&apos;s slow, the WhatsApp DM lands faster.
            </p>
          </Reveal>
        </div>
      </section>

      <CityStrip num="03" />

      {/* Local animated gradient keyframes — kept page-local, calm pace */}
      <style>{`
        @keyframes nio-contact-glow {
          0%   { transform: translate3d(0,0,0) scale(1); }
          50%  { transform: translate3d(2%, -1%, 0) scale(1.04); }
          100% { transform: translate3d(0,0,0) scale(1); }
        }
        .contact-glow {
          animation: nio-contact-glow 14s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .contact-glow { animation: none; }
        }
      `}</style>
    </div>
  );
}

/** Underline-style field. Error state swaps the rule to red. */
function fieldCls(error) {
  return [
    'peer block w-full bg-transparent border-0 border-b px-0 py-3 text-[17px] text-white rounded-none',
    'placeholder:text-ink-muted/50 focus:outline-none focus:ring-0 transition-colors duration-300',
    error ? 'border-[#FF7A7A]/70 focus:border-[#FF7A7A]' : 'border-white/15 hover:border-white/30 focus:border-white/40',
  ].join(' ');
}

function Field({ n, label, id, error, className = '', children }) {
  return (
    <div className={`block ${className}`}>
      <label htmlFor={id} className="nd-mono flex items-center gap-3 mb-1">
        <span className="text-white tabular-nums">{n}</span>
        {label}
      </label>
      <div className="relative">
        {children}
        <span
          aria-hidden
          className={`pointer-events-none absolute left-0 right-0 bottom-0 h-px origin-left scale-x-0 peer-focus:scale-x-100 transition-transform duration-500 [transition-timing-function:var(--ease-out)] ${
            error ? 'bg-[#FF7A7A]' : 'bg-[linear-gradient(90deg,#6C5CE7,#4B9FFF)]'
          }`}
        />
      </div>
      {error && (
        <p id={`${id}-err`} role="alert" className="mt-2.5 text-[13px] text-[#FF9A9A]">
          {error}
        </p>
      )}
    </div>
  );
}
